import React, { useEffect, useState } from "react";
import moment from "moment";
import {
  Button,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import CopyButton from "../global/CopyButton";

/*
    API Access (static#49) — D15, the ONE-TIME reveal.

    This modal is the delivery mechanism for the customer's credentials. AWS does
    not let anyone retrieve an IAM secret access key after creation — not
    Golfbert, not support, not AWS — so if this modal is dismissed without the
    secret being saved, the customer's only remedy is to rotate and start over.

    Consequences for this component, all deliberate:
      * `credentials` arrives as a prop from the parent's LOCAL STATE. It is
        never read from Redux, because src/store/index.js persists state to
        localStorage under a hardcoded AES key.
      * The modal is `backdrop="static"` with `keyboard={false}` and has no close
        "x" — a stray click on the page or an Escape keypress must not be able to
        destroy the only copy of the secret.
      * Dismissal requires ticking an explicit acknowledgement.
      * Nothing here logs. Do not add a console statement to this file.
*/
const ApiKeyReveal = ({
  isOpen,
  credentials,
  mode = "provision",
  prevExpiresAt = null,
  onAcknowledge,
}) => {
  const [acknowledged, setAcknowledged] = useState(false);

  // Reset the acknowledgement each time the modal opens, so the checkbox can
  // never arrive pre-ticked for a later reveal.
  useEffect(() => {
    if (isOpen) {
      setAcknowledged(false);
    }
  }, [isOpen]);

  const accessKeyId = credentials?.accessKeyId || "";
  const secretAccessKey = credentials?.secretAccessKey || "";
  const apigwKeyValue = credentials?.apigwKeyValue || "";

  const allFields = [
    `Access Key ID: ${accessKeyId}`,
    `Secret Access Key: ${secretAccessKey}`,
    `API Key (x-api-key): ${apigwKeyValue}`,
  ].join("\n");

  const isRotation = mode === "rotate";
  const previousExpiry =
    prevExpiresAt && moment(prevExpiresAt).isValid()
      ? moment(prevExpiresAt).format("MMMM D, YYYY")
      : null;

  return (
    <Modal
      className="contact-modal api-access-reveal"
      isOpen={!!isOpen}
      backdrop="static"
      keyboard={false}
      autoFocus
    >
      {/* No `toggle` prop: this header intentionally has no close "x". */}
      <ModalHeader className="contact-modal__head">
        <h4>
          {isRotation ? "Your new API credentials" : "Your API credentials"}
        </h4>
      </ModalHeader>
      <ModalBody className="contact-modal__modal-scrollable">
        <div
          className="api-access__alert api-access__alert--warning mb-4"
          role="alert"
        >
          <strong>
            This is the only time your Secret Access Key will be shown.
          </strong>{" "}
          Golfbert does not store it and cannot show it to you again. Copy it
          somewhere safe before you close this window. If you lose it, you will
          have to rotate your key and update your integration.
        </div>

        <p className="contact-modal__password-para mb-4">
          {isRotation ? (
            <>
              Use these credentials from now on.
              {previousExpiry ? (
                <>
                  {" "}
                  Your previous credentials keep working until{" "}
                  <strong>{previousExpiry}</strong>, so you have time to deploy
                  the change.
                </>
              ) : (
                <>
                  {" "}
                  Your previous credentials keep working for 7 more days, so you
                  have time to deploy the change.
                </>
              )}
            </>
          ) : (
            <>
              Requests to <code>api.golfbert.com</code> need all three of these:
              the Access Key ID and Secret Access Key sign the request, and the
              API Key goes in the <code>x-api-key</code> header.
            </>
          )}
        </p>

        <div className="api-access__credential mb-3">
          <Label
            for="api-access-reveal-access-key-id"
            className="contact-modal__modal-label d-block"
          >
            Access Key ID
          </Label>
          <div className="d-flex align-items-center">
            <Input
              id="api-access-reveal-access-key-id"
              className="signIn__input contact-modal__modal-input api-access__credential-input"
              type="text"
              value={accessKeyId}
              readOnly
              spellCheck={false}
              autoComplete="off"
              onFocus={(e) => e.target.select()}
            />
            <CopyButton
              value={accessKeyId}
              className="ml-2 flex-shrink-0"
              ariaLabel="Copy Access Key ID"
            />
          </div>
        </div>

        <div className="api-access__credential mb-3">
          <Label
            for="api-access-reveal-secret"
            className="contact-modal__modal-label d-block"
          >
            Secret Access Key
            <span className="api-access__badge api-access__badge--danger ml-2">
              shown once
            </span>
          </Label>
          <div className="d-flex align-items-center">
            <Input
              id="api-access-reveal-secret"
              className="signIn__input contact-modal__modal-input api-access__credential-input"
              type="text"
              value={secretAccessKey}
              readOnly
              spellCheck={false}
              autoComplete="off"
              onFocus={(e) => e.target.select()}
            />
            <CopyButton
              value={secretAccessKey}
              className="ml-2 flex-shrink-0"
              ariaLabel="Copy Secret Access Key"
            />
          </div>
        </div>

        <div className="api-access__credential mb-4">
          <Label
            for="api-access-reveal-apigw-key"
            className="contact-modal__modal-label d-block"
          >
            API Key (<code>x-api-key</code>)
          </Label>
          <div className="d-flex align-items-center">
            <Input
              id="api-access-reveal-apigw-key"
              className="signIn__input contact-modal__modal-input api-access__credential-input"
              type="text"
              value={apigwKeyValue}
              readOnly
              spellCheck={false}
              autoComplete="off"
              onFocus={(e) => e.target.select()}
            />
            <CopyButton
              value={apigwKeyValue}
              className="ml-2 flex-shrink-0"
              ariaLabel="Copy API Key"
            />
          </div>
          <small className="text-muted">
            You can see this one again on your Settings page at any time. The
            Secret Access Key you cannot.
          </small>
        </div>

        <div className="mb-4">
          <CopyButton
            value={allFields}
            label="Copy all three"
            copiedLabel="All three copied"
            ariaLabel="Copy all three credentials"
          />
        </div>

        <div className="api-access__ack">
          <Input
            type="checkbox"
            id="api-access-reveal-ack"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
          />
          <Label for="api-access-reveal-ack" className="mb-0">
            I have saved my Secret Access Key somewhere safe. I understand it
            cannot be shown again.
          </Label>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button type="button" onClick={onAcknowledge} disabled={!acknowledged}>
          Done
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ApiKeyReveal;
